/**
 * Máy chủ giả của token AI cấp thêm và yêu cầu cấp thêm (ADR-0091, tenant-service):
 * `/api/v1/tenants/{id}/ai-token-grants`, `/api/v1/tenants/{id}/ai-token-requests`,
 * `/api/v1/security/ai-token-requests`.
 *
 * Tính như backend: `status` của dòng cấp thêm suy ra từ hôm nay (giờ Việt Nam); duyệt yêu cầu sinh hoặc gia
 * hạn dòng cấp thêm trong cùng một bước; mỗi tenant tối đa 3 yêu cầu đang chờ. Ngày mẫu tính tương đối với
 * hôm nay để luôn có đủ trạng thái. Màu và nhãn không ở đây (`modules/tenant/constants.ts`).
 *
 * Tình huống mẫu để soát:
 * - FoxAI (`…0001`): gói tạm thời đang chạy, một lần nạp 3 tháng **sắp hết hạn** (còn 5 ngày), một lần nạp đã thu
 *   hồi, một lần nạp đã hết hạn;
 * - Minh Long (`…0002`, tenant của tài khoản mẫu cổng tenant, thứ tự trừ `MUA_THEM_TRUOC`): một lần nạp 3 tháng
 *   đang dùng dở, gói tạm thời bắt đầu tháng sau, hai yêu cầu đang chờ (gửi thêm hai cái thì cái thứ tư bị từ chối
 *   vì quá 3), một bị từ chối, một đã duyệt;
 * - Phương Nam (`…0007`, đã chặn): yêu cầu nạp thêm và gói tạm thời đang chờ.
 */
import type { ApiError, LoiTruong } from '../lib/errors';
import {
  congNgay,
  homNayVN,
  kiemCapThem,
  NGAY_SAP_HET_HAN,
  ngayKetThuc,
  YEU_CAU_CHO_TOI_DA,
} from '../modules/tenant/nghiep-vu/cap-them-token';
import {
  chuanSoTien,
  giaVnd,
  kiemSoTien,
  thoiHanHopLe as thoiHanNapHopLe,
  tokenTuSoTien,
} from '../modules/goi-nap-token/nghiep-vu/fox';
import { mayChuGoiNapToken, tyGiaUsdVnd } from './goi-nap-token';
import type {
  AiTokenDurationUnit,
  AiTokenTopUpSnapshot,
  AiTokenGrant,
  AiTokenGrantCreate,
  AiTokenGrantStatus,
  AiTokenPriority,
  AiTokenQuotaBucket,
  AiTokenRequest,
  AiTokenRequestApprove,
  AiTokenRequestCreate,
  AiTokenRequestStatus,
} from '../modules/tenant/types';

const FOXAI = '0a7d3c5e-1f2b-4e6a-9c8d-000000000001';
const MINH_LONG = '0a7d3c5e-1f2b-4e6a-9c8d-000000000002';
const PHUONG_NAM = '0a7d3c5e-1f2b-4e6a-9c8d-000000000007';

const TEN_TENANT: Record<string, string> = {
  [FOXAI]: 'FoxAI',
  [MINH_LONG]: 'Minh Long',
  [PHUONG_NAM]: 'Phương Nam',
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000003': 'Đại Phát Miền Nam',
};

const PA = '7f1c0000-0000-4000-8000-0000000000aa';

/** Ngày đầu tháng cách tháng của `ngay` `n` tháng. */
function dauThang(ngay: string, n: number): string {
  const [y, m] = ngay.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 10);
}

const HOM_NAY = homNayVN();
const luc = (ngay: string, gio = '09:00') => `${ngay}T${gio}:00+07:00`;

type DongCapThem = Omit<AiTokenGrant, 'status' | 'remaining_tokens'>;

let capThem: DongCapThem[] = [
  {
    id: 'a1000000-0000-4000-8000-000000000001',
    tenant_id: FOXAI,
    kind: 'GOI_TAM_THOI',
    tokens: 20_000_000,
    used_tokens: 6_420_000,
    valid_from: dauThang(HOM_NAY, -1),
    valid_to: ngayKetThuc(dauThang(HOM_NAY, -1), 'MONTH', 4),
    // Sinh từ một yêu cầu nạp token theo gói (ADR-0100): túi mang ảnh chụp giá lúc gửi
    request_id: 'b1000000-0000-4000-8000-000000000008',
    note: 'Bổ sung cho đợt số hoá hồ sơ lưu trữ quý IV',
    created_by: PA,
    created_at: luc(dauThang(HOM_NAY, -1)),
    revoked_at: null,
    revoked_by: null,
    top_up: {
      package_code: 'NAP_20_TRIEU',
      package_name: 'Nạp 20 triệu token',
      amount_usd: null,
      unit_price_usd: '2.5',
      price_usd: '45.00',
      exchange_rate: '25400', exchange_rate_id: 2, exchange_rate_date: '2026-10-01',
      price_vnd: '1143000',
    },
  },
  {
    id: 'a1000000-0000-4000-8000-000000000006',
    tenant_id: FOXAI,
    kind: 'NAP_THEM',
    tokens: 3_000_000,
    used_tokens: 1_150_000,
    valid_from: congNgay(HOM_NAY, -86),
    valid_to: congNgay(HOM_NAY, 5),
    request_id: null,
    note: 'Nạp 3 tháng cho dự án chuyển đổi số',
    created_by: PA,
    created_at: luc(congNgay(HOM_NAY, -86)),
    revoked_at: null,
    revoked_by: null,
  },
  {
    id: 'a1000000-0000-4000-8000-000000000002',
    tenant_id: FOXAI,
    kind: 'NAP_THEM',
    tokens: 5_000_000,
    used_tokens: 0,
    valid_from: congNgay(HOM_NAY, -3),
    valid_to: ngayKetThuc(congNgay(HOM_NAY, -3), 'MONTH', 1),
    request_id: null,
    note: 'Nạp nhầm số, đã thu hồi',
    created_by: PA,
    created_at: luc(congNgay(HOM_NAY, -3)),
    revoked_at: luc(congNgay(HOM_NAY, -3), '09:12'),
    revoked_by: PA,
  },
  {
    id: 'a1000000-0000-4000-8000-000000000003',
    tenant_id: FOXAI,
    kind: 'NAP_THEM',
    tokens: 10_000_000,
    used_tokens: 10_000_000,
    valid_from: congNgay(HOM_NAY, -40),
    valid_to: congNgay(HOM_NAY, -10),
    request_id: 'b1000000-0000-4000-8000-000000000009',
    note: null,
    created_by: PA,
    created_at: luc(congNgay(HOM_NAY, -40)),
    revoked_at: null,
    revoked_by: null,
  },
  {
    id: 'a1000000-0000-4000-8000-000000000007',
    tenant_id: MINH_LONG,
    kind: 'NAP_THEM',
    tokens: 500_000,
    used_tokens: 320_000,
    valid_from: congNgay(HOM_NAY, -20),
    valid_to: ngayKetThuc(congNgay(HOM_NAY, -20), 'MONTH', 3),
    request_id: null,
    note: 'Nạp 3 tháng cho bộ phận kế toán',
    created_by: PA,
    created_at: luc(congNgay(HOM_NAY, -20)),
    revoked_at: null,
    revoked_by: null,
  },
  {
    id: 'a1000000-0000-4000-8000-000000000004',
    tenant_id: MINH_LONG,
    kind: 'GOI_TAM_THOI',
    tokens: 1_500_000,
    used_tokens: 0,
    valid_from: dauThang(HOM_NAY, 1),
    valid_to: ngayKetThuc(dauThang(HOM_NAY, 1), 'MONTH', 3),
    request_id: 'b1000000-0000-4000-8000-000000000004',
    note: 'Theo yêu cầu của tổ chức, mùa báo cáo cuối năm',
    created_by: PA,
    created_at: luc(congNgay(HOM_NAY, -6)),
    revoked_at: null,
    revoked_by: null,
  },
  {
    id: 'a1000000-0000-4000-8000-000000000005',
    tenant_id: MINH_LONG,
    kind: 'NAP_THEM',
    tokens: 300_000,
    used_tokens: 300_000,
    valid_from: dauThang(HOM_NAY, -2),
    valid_to: ngayKetThuc(dauThang(HOM_NAY, -2), 'MONTH', 1),
    request_id: null,
    note: null,
    created_by: PA,
    created_at: luc(dauThang(HOM_NAY, -2)),
    revoked_at: null,
    revoked_by: null,
  },
];

/** Thứ tự trừ token theo tenant (`ai_quota.uu_tien`); không có dòng là mặc định `HET_HAN_TRUOC`. */
const uuTien = new Map<string, AiTokenPriority>([[MINH_LONG, 'MUA_THEM_TRUOC']]);

let yeuCau: AiTokenRequest[] = [
  {
    id: 'b1000000-0000-4000-8000-000000000001',
    tenant_id: MINH_LONG,
    tenant_name: TEN_TENANT[MINH_LONG],
    kind: 'NAP_TOKEN',
    tokens: 5_000_000,
    duration_unit: 'MONTH',
    duration_count: 1,
    valid_to: null,
    grant_id: null,
    reason: 'Phòng Kế toán cần nhận dạng thêm khoảng 300 hoá đơn đầu vào trước kỳ quyết toán thuế.',
    top_up: {
      package_code: 'NAP_5_TRIEU',
      package_name: 'Nạp 5 triệu token',
      amount_usd: null,
      unit_price_usd: '2.5',
      price_usd: '12.50',
      exchange_rate: '25400', exchange_rate_id: 2, exchange_rate_date: '2026-10-01',
      price_vnd: '317500',
    },
    status: 'PENDING',
    requested_by: 'u-ml-0001',
    requested_by_email: 'tran.thu@minhlong.vn',
    requested_at: luc(congNgay(HOM_NAY, -1), '15:42'),
    decided_by: null,
    decided_at: null,
    decision_note: null,
    approved_tokens: null,
    approved_valid_from: null,
    approved_valid_to: null,
  },
  {
    id: 'b1000000-0000-4000-8000-000000000002',
    tenant_id: MINH_LONG,
    tenant_name: TEN_TENANT[MINH_LONG],
    kind: 'GIA_HAN',
    tokens: null,
    duration_unit: null,
    duration_count: null,
    valid_to: ngayKetThuc(dauThang(HOM_NAY, 1), 'MONTH', 6),
    grant_id: 'a1000000-0000-4000-8000-000000000004',
    reason: 'Kéo dài gói tạm thời tới hết quý I để xử lý hồ sơ tồn.',
    status: 'PENDING',
    requested_by: 'u-ml-0001',
    requested_by_email: 'tran.thu@minhlong.vn',
    requested_at: luc(HOM_NAY, '08:15'),
    decided_by: null,
    decided_at: null,
    decision_note: null,
    approved_tokens: null,
    approved_valid_from: null,
    approved_valid_to: null,
  },
  {
    id: 'b1000000-0000-4000-8000-000000000003',
    tenant_id: MINH_LONG,
    tenant_name: TEN_TENANT[MINH_LONG],
    kind: 'NAP_THEM',
    tokens: 5_000_000,
    duration_unit: 'MONTH',
    duration_count: 12,
    valid_to: null,
    grant_id: null,
    reason: 'Thử nghiệm tóm tắt toàn bộ kho văn bản.',
    status: 'REJECTED',
    requested_by: 'u-ml-0002',
    requested_by_email: 'le.nam@minhlong.vn',
    requested_at: luc(congNgay(HOM_NAY, -12), '10:02'),
    decided_by: PA,
    decided_at: luc(congNgay(HOM_NAY, -11), '14:30'),
    decision_note: 'Số xin vượt nhiều lần hạn mức gói Dùng thử. Đề nghị nâng gói thay vì nạp thêm.',
    approved_tokens: null,
    approved_valid_from: null,
    approved_valid_to: null,
  },
  {
    id: 'b1000000-0000-4000-8000-000000000004',
    tenant_id: MINH_LONG,
    tenant_name: TEN_TENANT[MINH_LONG],
    kind: 'GOI_TAM_THOI',
    tokens: 2_000_000,
    duration_unit: 'MONTH',
    duration_count: 3,
    valid_to: null,
    grant_id: 'a1000000-0000-4000-8000-000000000004',
    reason: 'Mùa báo cáo cuối năm, cần thêm AI cho bộ phận tổng hợp.',
    status: 'APPROVED',
    requested_by: 'u-ml-0001',
    requested_by_email: 'tran.thu@minhlong.vn',
    requested_at: luc(congNgay(HOM_NAY, -8), '09:20'),
    decided_by: PA,
    decided_at: luc(congNgay(HOM_NAY, -6), '11:05'),
    decision_note: 'Duyệt 1.500.000 token trọn gói từ đầu tháng sau, đủ cho khối lượng đã nêu.',
    approved_tokens: 1_500_000,
    approved_valid_from: dauThang(HOM_NAY, 1),
    approved_valid_to: ngayKetThuc(dauThang(HOM_NAY, 1), 'MONTH', 3),
  },
  {
    id: 'b1000000-0000-4000-8000-000000000005',
    tenant_id: PHUONG_NAM,
    tenant_name: TEN_TENANT[PHUONG_NAM],
    kind: 'NAP_TOKEN',
    tokens: 16_000_000,
    duration_unit: 'MONTH',
    duration_count: 3,
    top_up: {
      package_code: null,
      package_name: null,
      amount_usd: '40.00',
      unit_price_usd: '2.5',
      price_usd: '40.00',
      exchange_rate: '25400', exchange_rate_id: 2, exchange_rate_date: '2026-10-01',
      price_vnd: '1016000',
    },
    valid_to: null,
    grant_id: null,
    reason: 'Đã hết hạn mức tháng, dây chuyền thẩm định hồ sơ vay đang dừng. Cần mở lại trong ngày.',
    status: 'PENDING',
    requested_by: 'u-pn-0001',
    requested_by_email: 'quan.tri@phuongnam.com.vn',
    requested_at: luc(HOM_NAY, '07:48'),
    decided_by: null,
    decided_at: null,
    decision_note: null,
    approved_tokens: null,
    approved_valid_from: null,
    approved_valid_to: null,
  },
  {
    id: 'b1000000-0000-4000-8000-000000000006',
    tenant_id: PHUONG_NAM,
    tenant_name: TEN_TENANT[PHUONG_NAM],
    kind: 'GOI_TAM_THOI',
    tokens: 20_000_000,
    duration_unit: 'WEEK',
    duration_count: 4,
    valid_to: null,
    grant_id: null,
    reason: 'Gói 20 triệu token trong bốn tuần cao điểm thẩm định hồ sơ vay cuối năm.',
    status: 'PENDING',
    requested_by: 'u-pn-0001',
    requested_by_email: 'quan.tri@phuongnam.com.vn',
    requested_at: luc(congNgay(HOM_NAY, -2), '16:10'),
    decided_by: null,
    decided_at: null,
    decision_note: null,
    approved_tokens: null,
    approved_valid_from: null,
    approved_valid_to: null,
  },
  {
    id: 'b1000000-0000-4000-8000-000000000007',
    tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000003',
    tenant_name: TEN_TENANT['0a7d3c5e-1f2b-4e6a-9c8d-000000000003'],
    kind: 'GOI_TAM_THOI',
    tokens: 2_000_000,
    duration_unit: 'WEEK',
    duration_count: 2,
    valid_to: null,
    grant_id: null,
    reason: 'Chiến dịch số hoá hợp đồng lao động tháng tới.',
    status: 'CANCELLED',
    requested_by: 'u-dp-0001',
    requested_by_email: 'it@daiphat.vn',
    requested_at: luc(congNgay(HOM_NAY, -4), '13:00'),
    decided_by: 'u-dp-0001',
    decided_at: luc(congNgay(HOM_NAY, -4), '13:40'),
    decision_note: null,
    approved_tokens: null,
    approved_valid_from: null,
    approved_valid_to: null,
  },
];

function loi(status: number, code: string, message: string, details?: LoiTruong[]): ApiError {
  return { status, code, message, ...(details ? { details } : {}) };
}

function loiTruong(pointer: string, detail: string): ApiError {
  return loi(400, 'INVALID_ARGUMENT', 'Thông tin chưa đúng, vui lòng sửa trường được đánh dấu.', [
    { pointer, detail },
  ]);
}

/**
 * Yêu cầu nạp token (ADR-0100 mục 5) như tenant-service: theo gói (gói đang bán, số token, thời hạn, giá của gói)
 * hoặc theo số tiền (1–1.000.000 USD, token = floor(số tiền / đơn giá × 1e6)); ảnh chụp ghi đơn giá, giá, tỷ giá.
 */
function yeuCauNapToken(du: AiTokenRequestCreate): {
  tokens: number;
  donVi: AiTokenDurationUnit;
  so: number;
  chup: AiTokenTopUpSnapshot;
} {
  if (Boolean(du.package_code) === Boolean(du.amount_usd)) {
    throw loiTruong(du.package_code ? '#/amount_usd' : '#/package_code', 'Chọn một gói nạp token hoặc nhập số tiền muốn nạp, không gửi cả hai.');
  }
  const donGia = mayChuGoiNapToken.donGia();
  const ty = tyGiaUsdVnd();
  const chup = (code: string | null, ten: string | null, tien: string | null, gia: string): AiTokenTopUpSnapshot => ({
    package_code: code,
    package_name: ten,
    amount_usd: tien,
    unit_price_usd: donGia,
    price_usd: gia,
    exchange_rate: ty?.rate ?? null,
    exchange_rate_id: ty?.rate_id ?? null,
    exchange_rate_date: ty?.effective_from ?? null,
    price_vnd: giaVnd(gia, ty?.rate),
  });
  if (du.package_code) {
    const g = mayChuGoiNapToken.tim(du.package_code);
    if (!g) throw loiTruong('#/package_code', 'Mã này là gói hạn mức của tổ chức, không xin thêm được. Chọn một gói nạp token.');
    if (!g.is_active) throw loiTruong('#/package_code', `Gói ${g.name} đã ngừng bán. Chọn gói khác hoặc nạp theo số tiền.`);
    if (!g.effective_price_usd) {
      throw loiTruong('#/package_code', 'Gói này chưa có giá vì nền tảng chưa đặt đơn giá token Fox. Liên hệ quản trị nền tảng.');
    }
    return { tokens: g.fox_tokens, donVi: g.validity_unit, so: g.validity_count, chup: chup(g.code, g.name, null, g.effective_price_usd) };
  }
  const tien = chuanSoTien(du.amount_usd ?? '');
  if (kiemSoTien(tien)) throw loiTruong('#/amount_usd', 'Số tiền nạp phải từ 1 tới 1.000.000 USD, tối đa 2 chữ số thập phân.');
  if (!donGia) {
    throw loiTruong('#/amount_usd', 'Nền tảng chưa đặt đơn giá token Fox nên chưa nạp theo số tiền được. Chọn một gói nạp token.');
  }
  const donVi = du.duration_unit ?? 'MONTH';
  const so = du.duration_count ?? 1;
  if (!thoiHanNapHopLe(donVi, so)) throw loiTruong('#/duration_count', 'Thời hạn là 1, 3, 6, 12 tháng hoặc 1 tới 4 tuần.');
  const gia = Number(tien).toFixed(2);
  return { tokens: tokenTuSoTien(tien, donGia) ?? 0, donVi, so, chup: chup(null, null, gia, gia) };
}

function trangThai(g: DongCapThem, homNay: string): AiTokenGrantStatus {
  if (g.revoked_at) return 'REVOKED';
  if (homNay < g.valid_from) return 'SCHEDULED';
  if (homNay > g.valid_to) return 'EXPIRED';
  return 'ACTIVE';
}

const ra = (g: DongCapThem): AiTokenGrant => ({
  ...g,
  remaining_tokens: Math.max(0, g.tokens - g.used_tokens),
  status: trangThai(g, homNayVN()),
});

const moiTruoc = <T extends { created_at?: string; requested_at?: string; id: string }>(a: T, b: T) =>
  (b.created_at ?? b.requested_at ?? '').localeCompare(a.created_at ?? a.requested_at ?? '') || b.id.localeCompare(a.id);

function timYeuCau(id: string): AiTokenRequest {
  const r = yeuCau.find((x) => x.id === id);
  if (!r) throw loi(404, 'NOT_FOUND', 'Không tìm thấy yêu cầu.');
  return r;
}

function canCho(r: AiTokenRequest): void {
  if (r.status !== 'PENDING') {
    throw loiTruong('#/status', 'Yêu cầu đã được xử lý hoặc đã huỷ; tải lại danh sách để xem trạng thái mới.');
  }
}

function doiYeuCau(id: string, sua: Partial<AiTokenRequest>): AiTokenRequest {
  yeuCau = yeuCau.map((r) => (r.id === id ? { ...r, ...sua } : r));
  return timYeuCau(id);
}

export const mayChuCapThemToken = {
  /** Túi cấp thêm còn hiệu lực hôm nay theo `valid_to` tăng dần — `grants` của hạn mức. */
  tuiDangHieuLuc(tenantId: string): AiTokenQuotaBucket[] {
    const homNay = homNayVN();
    return capThem
      .filter((g) => g.tenant_id === tenantId && trangThai(g, homNay) === 'ACTIVE')
      .sort((a, b) => a.valid_to.localeCompare(b.valid_to))
      .map((g) => ({
        grant_id: g.id,
        kind: g.kind,
        tokens: g.tokens,
        used: g.used_tokens,
        remaining: Math.max(0, g.tokens - g.used_tokens),
        valid_from: g.valid_from,
        valid_to: g.valid_to,
        expiring_soon: g.valid_to <= congNgay(homNay, NGAY_SAP_HET_HAN),
      }));
  },

  uuTienCua(tenantId: string): AiTokenPriority {
    return uuTien.get(tenantId) ?? 'HET_HAN_TRUOC';
  },

  datUuTien(tenantId: string, p: unknown): void {
    if (p !== 'HET_HAN_TRUOC' && p !== 'MUA_THEM_TRUOC') {
      throw loiTruong('#/priority', 'Chọn một trong hai thứ tự dùng token.');
    }
    uuTien.set(tenantId, p);
  },

  capThemCua(tenantId: string): { items: AiTokenGrant[]; next_cursor: null } {
    return { items: capThem.filter((g) => g.tenant_id === tenantId).sort(moiTruoc).map(ra), next_cursor: null };
  },

  taoCapThem(
    tenantId: string,
    du: AiTokenGrantCreate,
    boi = PA,
    requestId: string | null = null,
    topUp: AiTokenTopUpSnapshot | null = null,
  ): AiTokenGrant {
    const kq = kiemCapThem(
      {
        kind: du.kind,
        tokens: String(du.tokens ?? ''),
        validFrom: du.valid_from ?? '',
        donVi: du.duration_unit ?? 'MONTH',
        soLuong: du.duration_count ?? 1,
        validTo: du.valid_to ?? '',
      },
      homNayVN(),
    );
    if (!kq.than) {
      if (kq.loi.tokens) throw loiTruong('#/tokens', 'Số token phải là số nguyên từ 1 đến 1.000.000.000.000.');
      if (kq.loi.thoiHan) {
        throw loiTruong(
          '#/duration_count',
          du.kind === 'NAP_THEM'
            ? 'Nạp thêm có thời hạn 1, 3, 6 hoặc 12 tháng.'
            : 'Gói tạm thời có thời hạn 1–4 tuần hoặc 1–12 tháng.',
        );
      }
      throw loiTruong('#/valid_to', 'Ngày kết thúc phải từ hôm nay trở đi và không trước ngày bắt đầu.');
    }
    const dong: DongCapThem = {
      id: crypto.randomUUID(),
      tenant_id: tenantId,
      kind: kq.than.kind,
      tokens: kq.than.tokens,
      used_tokens: 0,
      valid_from: kq.khoang.tu,
      valid_to: kq.khoang.den,
      request_id: requestId,
      note: du.note?.trim() || null,
      created_by: boi,
      created_at: new Date().toISOString(),
      revoked_at: null,
      revoked_by: null,
      top_up: topUp,
    };
    capThem = [dong, ...capThem];
    return ra(dong);
  },

  giaHan(tenantId: string, grantId: string, validTo: string, note?: string | null): AiTokenGrant {
    const g = capThem.find((x) => x.id === grantId && x.tenant_id === tenantId);
    if (!g) throw loi(404, 'NOT_FOUND', 'Không tìm thấy dòng cấp thêm.');
    const st = trangThai(g, homNayVN());
    if (g.kind !== 'GOI_TAM_THOI' || st === 'REVOKED') {
      throw loiTruong('#/valid_to', 'Chỉ gia hạn được gói tạm thời chưa thu hồi.');
    }
    if (!validTo || validTo <= g.valid_to || validTo < homNayVN()) {
      throw loiTruong('#/valid_to', 'Ngày kết thúc mới phải sau ngày kết thúc hiện tại và không trước hôm nay.');
    }
    capThem = capThem.map((x) =>
      x.id === grantId ? { ...x, valid_to: validTo, note: note?.trim() ? note.trim() : x.note } : x,
    );
    return ra(capThem.find((x) => x.id === grantId) as DongCapThem);
  },

  thuHoi(tenantId: string, grantId: string): AiTokenGrant {
    const g = capThem.find((x) => x.id === grantId && x.tenant_id === tenantId);
    if (!g) throw loi(404, 'NOT_FOUND', 'Không tìm thấy dòng cấp thêm.');
    if (g.revoked_at) throw loiTruong('#/status', 'Dòng này đã được thu hồi.');
    capThem = capThem.map((x) =>
      x.id === grantId ? { ...x, revoked_at: new Date().toISOString(), revoked_by: PA } : x,
    );
    return ra(capThem.find((x) => x.id === grantId) as DongCapThem);
  },

  yeuCauCua(tenantId: string): { items: AiTokenRequest[]; next_cursor: null } {
    return { items: yeuCau.filter((r) => r.tenant_id === tenantId).sort(moiTruoc), next_cursor: null };
  },

  guiYeuCau(tenantId: string, du: AiTokenRequestCreate): AiTokenRequest {
    const dangCho = yeuCau.filter((r) => r.tenant_id === tenantId && r.status === 'PENDING').length;
    if (dangCho >= YEU_CAU_CHO_TOI_DA) {
      throw loiTruong(
        '#/kind',
        `Tổ chức đã có ${YEU_CAU_CHO_TOI_DA} yêu cầu đang chờ duyệt. Chờ quản trị nền tảng xử lý hoặc huỷ bớt một yêu cầu rồi gửi lại.`,
      );
    }
    const napToken = du.kind === undefined || du.kind === 'NAP_TOKEN';
    if (!napToken && !du.reason?.trim()) throw loiTruong('#/reason', 'Nhập lý do cần thêm token.');
    if (du.kind === 'GIA_HAN' && !du.grant_id) throw loiTruong('#/grant_id', 'Chọn gói tạm thời cần gia hạn.');
    const nap = napToken ? yeuCauNapToken(du) : null;
    const kind: AiTokenRequest['kind'] = napToken ? 'NAP_TOKEN' : (du.kind as AiTokenRequest['kind']);
    const moi: AiTokenRequest = {
      id: crypto.randomUUID(),
      tenant_id: tenantId,
      tenant_name: TEN_TENANT[tenantId] ?? 'Tenant',
      kind,
      tokens: nap?.tokens ?? du.tokens ?? null,
      duration_unit: nap?.donVi ?? du.duration_unit ?? (kind === 'GIA_HAN' ? null : 'MONTH'),
      duration_count: nap?.so ?? du.duration_count ?? (kind === 'GIA_HAN' ? null : 1),
      valid_to: du.valid_to ?? null,
      grant_id: du.grant_id ?? null,
      reason: du.reason?.trim() || null,
      top_up: nap?.chup ?? null,
      status: 'PENDING',
      requested_by: 'u-ml-0001',
      requested_by_email: 'tran.thu@minhlong.vn',
      requested_at: new Date().toISOString(),
      decided_by: null,
      decided_at: null,
      decision_note: null,
      approved_tokens: null,
      approved_valid_from: null,
      approved_valid_to: null,
    };
    yeuCau = [moi, ...yeuCau];
    return moi;
  },

  huyYeuCau(tenantId: string, id: string): AiTokenRequest {
    const r = timYeuCau(id);
    if (r.tenant_id !== tenantId) throw loi(404, 'NOT_FOUND', 'Không tìm thấy yêu cầu.');
    canCho(r);
    return doiYeuCau(id, { status: 'CANCELLED', decided_by: r.requested_by, decided_at: new Date().toISOString() });
  },

  hangCho(status: AiTokenRequestStatus | undefined): { items: AiTokenRequest[]; next_cursor: null; pending_count: number } {
    return {
      items: yeuCau.filter((r) => !status || r.status === status).sort(moiTruoc),
      next_cursor: null,
      pending_count: yeuCau.filter((r) => r.status === 'PENDING').length,
    };
  },

  duyet(id: string, du: AiTokenRequestApprove): AiTokenRequest {
    const r = timYeuCau(id);
    canCho(r);
    const ghiChu = du.note?.trim() || null;
    const xong = new Date().toISOString();
    if (r.kind === 'GIA_HAN') {
      const den = du.valid_to ?? r.valid_to ?? '';
      mayChuCapThemToken.giaHan(r.tenant_id, r.grant_id ?? '', den);
      return doiYeuCau(id, {
        status: 'APPROVED',
        decided_by: PA,
        decided_at: xong,
        decision_note: ghiChu,
        approved_valid_to: den,
      });
    }
    const g = mayChuCapThemToken.taoCapThem(
      r.tenant_id,
      {
        kind: r.kind === 'NAP_TOKEN' ? 'GOI_TAM_THOI' : r.kind,
        tokens: du.tokens ?? r.tokens ?? 0,
        valid_from: du.valid_from ?? null,
        valid_to: du.valid_to ?? null,
        duration_unit: du.duration_unit ?? r.duration_unit,
        duration_count: du.duration_count ?? r.duration_count,
        note: ghiChu,
      },
      PA,
      r.id,
      r.top_up ?? null,
    );
    return doiYeuCau(id, {
      status: 'APPROVED',
      grant_id: g.id,
      decided_by: PA,
      decided_at: xong,
      decision_note: ghiChu,
      approved_tokens: g.tokens,
      approved_valid_from: g.valid_from,
      approved_valid_to: g.valid_to,
    });
  },

  tuChoi(id: string, note: string): AiTokenRequest {
    const r = timYeuCau(id);
    canCho(r);
    if (!note?.trim()) throw loiTruong('#/note', 'Nhập lý do từ chối; lý do được gửi tới quản trị tổ chức.');
    return doiYeuCau(id, {
      status: 'REJECTED',
      decided_by: PA,
      decided_at: new Date().toISOString(),
      decision_note: note.trim(),
    });
  },
};
